import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Instagram, Loader2, Mail, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const OBJECTIVES = [
  "Più clienti",
  "Più recensioni su Google",
  "Migliorare i social media",
  "Migliorare l'immagine del brand",
  "Non lo so, consigliami tu",
];

const CONTACT_METHODS = [
  { value: "Telefono", icon: Phone },
  { value: "Email", icon: Mail },
  { value: "WhatsApp", icon: MessageCircle },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const SOURCE_MAP = {
  qr: "Physical QR Card",
  instagram: "Instagram",
  direct: "Direct Link",
  website: "Website",
};

const initial = {
  business_name: "",
  industry: "",
  city: "",
  instagram_url: "",
  website_url: "",
  preferred_contact_method: "",
  contact_info: "",
  main_objective: "",
};

const FieldError = ({ id, message }) =>
  message ? (
    <p id={id} data-testid={`error-${id}`} className="text-xs text-red-400 mt-1.5 flex items-center gap-1.5">
      <span className="inline-block w-1 h-1 rounded-full bg-red-400 shrink-0" />
      {message}
    </p>
  ) : null;

export const LeadForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((errs) => (errs[key] ? { ...errs, [key]: undefined } : errs));
  };

  const pick = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((errs) => {
      const next = { ...errs, [key]: undefined };
      if (key === "preferred_contact_method") next.contact_info = undefined;
      return next;
    });
  };

  const validate = () => {
    const errs = {};
    if (form.business_name.trim().length < 2) errs.business_name = "Inserisci il nome della tua attività.";
    if (form.industry.trim().length < 2) errs.industry = "Inserisci il settore della tua attività.";
    if (form.city.trim().length < 2) errs.city = "Inserisci la città della tua attività.";
    if (!form.preferred_contact_method) errs.preferred_contact_method = "Scegli come preferisci essere ricontattato.";
    const info = form.contact_info.trim();
    if (form.preferred_contact_method === "Email") {
      if (!EMAIL_RE.test(info)) errs.contact_info = "Inserisci un indirizzo email valido.";
    } else if (form.preferred_contact_method === "Telefono" || form.preferred_contact_method === "WhatsApp") {
      if (info.replace(/\D/g, "").length < 6) errs.contact_info = "Inserisci un numero di telefono valido.";
    }
    if (!form.main_objective) errs.main_objective = "Scegli il tuo obiettivo principale.";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      const firstError = document.querySelector("[data-has-error='true']");
      firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      const srcParam = (searchParams.get("src") || "").toLowerCase();
      const source = SOURCE_MAP[srcParam] || "Physical QR Card";
      const { data } = await api.post("/leads", { ...form, source });
      if (data.listino_token) {
        sessionStorage.setItem("dc_listino_token", data.listino_token);
        sessionStorage.setItem("dc_business", data.business_name);
      }
      navigate("/conferma", { state: { businessName: data.business_name } });
    } catch (err) {
      toast.error(formatApiError(err, "Invio non riuscito. Controlla i campi e riprova."));
      setSubmitting(false);
    }
  };

  const isPhoneMethod = form.preferred_contact_method === "Telefono" || form.preferred_contact_method === "WhatsApp";

  return (
    <form onSubmit={submit} noValidate className="space-y-7" data-testid="lead-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2" data-has-error={!!errors.business_name}>
          <Label htmlFor="business_name" className="text-zinc-300 text-sm">Nome attività *</Label>
          <Input id="business_name" data-testid="lead-form-business-name" value={form.business_name} onChange={set("business_name")} placeholder="Es. Osteria da Marco" className={`input-lux ${errors.business_name ? "border-red-500/70" : ""}`} />
          <FieldError id="business-name" message={errors.business_name} />
        </div>
        <div className="space-y-2" data-has-error={!!errors.industry}>
          <Label htmlFor="industry" className="text-zinc-300 text-sm">Settore *</Label>
          <Input id="industry" data-testid="lead-form-industry" value={form.industry} onChange={set("industry")} placeholder="Es. Ristorazione, Boutique, Estetica" className={`input-lux ${errors.industry ? "border-red-500/70" : ""}`} />
          <FieldError id="industry" message={errors.industry} />
        </div>
        <div className="space-y-2" data-has-error={!!errors.city}>
          <Label htmlFor="city" className="text-zinc-300 text-sm">Città *</Label>
          <Input id="city" data-testid="lead-form-city" value={form.city} onChange={set("city")} placeholder="Es. Bologna" className={`input-lux ${errors.city ? "border-red-500/70" : ""}`} />
          <FieldError id="city" message={errors.city} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="instagram_url" className="text-zinc-300 text-sm flex items-center gap-1.5">
            <Instagram className="w-3.5 h-3.5 text-gold/80" /> Profilo Instagram <span className="text-zinc-600 font-normal">(facoltativo)</span>
          </Label>
          <Input id="instagram_url" data-testid="lead-form-instagram" value={form.instagram_url} onChange={set("instagram_url")} placeholder="https://instagram.com/tuaattivita" className="input-lux" />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="website_url" className="text-zinc-300 text-sm">Sito web o scheda Google Business <span className="text-zinc-600 font-normal">(facoltativo)</span></Label>
          <Input id="website_url" data-testid="lead-form-website" value={form.website_url} onChange={set("website_url")} placeholder="https://…" className="input-lux" />
        </div>
      </div>

      <div className="space-y-3" data-has-error={!!errors.preferred_contact_method}>
        <Label className="text-zinc-300 text-sm">Come preferisci essere ricontattato? *</Label>
        <div className={`grid grid-cols-3 gap-2 sm:gap-3 rounded-xl ${errors.preferred_contact_method ? "ring-1 ring-red-500/60 p-1.5" : ""}`} data-testid="lead-form-contact-method">
          {CONTACT_METHODS.map(({ value, icon: Icon }) => (
            <button
              key={value}
              type="button"
              data-testid={`contact-method-${value.toLowerCase()}`}
              onClick={() => pick("preferred_contact_method", value)}
              className={`flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2 rounded-xl border px-3 py-3 text-sm transition-all duration-300 ${
                form.preferred_contact_method === value
                  ? "border-gold/70 bg-gold/10 text-gold-light shadow-[0_0_24px_-6px_rgba(212,160,60,0.5)]"
                  : "border-white/10 bg-white/[0.03] text-zinc-400 hover:border-white/25 hover:text-slate-200"
              }`}
            >
              <Icon className="w-4 h-4" />
              {value}
            </button>
          ))}
        </div>
        <FieldError id="contact-method" message={errors.preferred_contact_method} />
      </div>

      <div className="space-y-2" data-has-error={!!errors.contact_info}>
        <Label htmlFor="contact_info" className="text-zinc-300 text-sm">
          {form.preferred_contact_method === "Email"
            ? "La tua email *"
            : isPhoneMethod
              ? "Il tuo numero di telefono *"
              : "Email o numero di telefono *"}
        </Label>
        <Input
          id="contact_info"
          data-testid="lead-form-contact-info"
          value={form.contact_info}
          onChange={set("contact_info")}
          inputMode={form.preferred_contact_method === "Email" ? "email" : isPhoneMethod ? "tel" : "text"}
          placeholder={form.preferred_contact_method === "Email" ? "nome@esempio.it" : "+39 333 000 0000"}
          className={`input-lux ${errors.contact_info ? "border-red-500/70" : ""}`}
        />
        <FieldError id="contact-info" message={errors.contact_info} />
      </div>

      <div className="space-y-3" data-has-error={!!errors.main_objective}>
        <Label className="text-zinc-300 text-sm">Qual è il tuo obiettivo principale? *</Label>
        <div className={`flex flex-wrap gap-2 rounded-xl ${errors.main_objective ? "ring-1 ring-red-500/60 p-1.5" : ""}`} data-testid="lead-form-objective">
          {OBJECTIVES.map((o) => (
            <button
              key={o}
              type="button"
              data-testid={`objective-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => pick("main_objective", o)}
              className={`rounded-full border px-4 py-2.5 text-sm transition-all duration-300 ${
                form.main_objective === o
                  ? "border-gold/70 bg-gold/10 text-gold-light shadow-[0_0_24px_-6px_rgba(212,160,60,0.5)]"
                  : "border-white/10 bg-white/[0.03] text-zinc-400 hover:border-white/25 hover:text-slate-200"
              }`}
            >
              {o}
            </button>
          ))}
        </div>
        <FieldError id="main-objective" message={errors.main_objective} />
      </div>

      <Button
        type="submit"
        disabled={submitting}
        data-testid="lead-form-submit-button"
        className="w-full h-14 rounded-xl bg-gradient-to-b from-gold-light via-gold to-gold-dark text-black font-extrabold text-base uppercase tracking-wide hover:shadow-[0_0_40px_-8px_rgba(212,160,60,0.8)] transition-all duration-300 disabled:opacity-60"
      >
        {submitting ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <span className="flex items-center gap-2">
            Invia la richiesta <ArrowRight className="w-5 h-5" />
          </span>
        )}
      </Button>
      <p className="text-center text-[11px] text-zinc-600 leading-relaxed">
        I dati inseriti saranno utilizzati esclusivamente per ricontattarti in merito alla tua richiesta.{" "}
        <Link to="/privacy" data-testid="form-privacy-link" className="text-gold/80 underline underline-offset-2 hover:text-gold-light">
          Privacy
        </Link>
      </p>
    </form>
  );
};
